import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Book, 
  BookOpen, 
  Crown,
  ArrowLeft,
  Check,
  Image,
  Palette,
  Quote,
  Eye,
  ShoppingCart,
  Loader2,
  Package,
  ChevronRight,
  AlertCircle,
  Upload,
  Camera,
  Type,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { doc, getDoc, updateDoc, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';

const BOOK_TIERS = [
  {
    id: 'mini',
    name: 'Mini Keepsake',
    description: 'A beautiful 12-page photo card capturing highlights',
    pages: 12,
    price: 29,
    icon: Book,
    features: [
      'Cover with photo',
      'Biography summary',
      '3 chapters',
      'Family photos'
    ],
    recommended: false
  },
  {
    id: 'standard',
    name: 'Standard Book',
    description: 'A complete 32-page memoir with full life story',
    pages: 32,
    price: 79,
    icon: BookOpen,
    features: [
      'Everything in Mini',
      '5 narrative chapters',
      'Pull quotes throughout',
      'Family contributions',
      'Photo gallery'
    ],
    recommended: true
  },
  {
    id: 'deluxe',
    name: 'Deluxe Edition',
    description: 'Premium 48-page hardcover with extra features',
    pages: 48,
    price: 129,
    icon: Crown,
    features: [
      'Everything in Standard',
      '8 narrative chapters',
      'Hardcover binding',
      'Extended photo gallery',
      'Family tree page',
      'Legacy & wisdom section'
    ],
    recommended: false
  }
];

const COLOR_SCHEMES = [
  { id: 'classic', name: 'Classic', primary: '#161845', accent: '#690FC9', bg: '#F9FAFB' },
  { id: 'warm', name: 'Warm Earth', primary: '#5D4037', accent: '#8D6E63', bg: '#FFF8E1' },
  { id: 'navy', name: 'Navy Gold', primary: '#1A237E', accent: '#FFD700', bg: '#E8EAF6' },
  { id: 'forest', name: 'Forest', primary: '#1B5E20', accent: '#4CAF50', bg: '#E8F5E9' },
  { id: 'burgundy', name: 'Burgundy', primary: '#6D1F3A', accent: '#C2185B', bg: '#FCE4EC' }
];

const TITLE_POSITIONS = [
  { id: 'bottom', name: 'Bottom Center', description: 'Title at the bottom of the cover' },
  { id: 'top', name: 'Top Center', description: 'Title at the top of the cover' },
  { id: 'center', name: 'Centered', description: 'Title centered on the cover' }
];

export default function PrintShopScreen() {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [story, setStory] = useState(null);
  const [contributions, setContributions] = useState([]);
  
  // Step state: 'tier' | 'narrative' | 'customize' | 'preview'
  const [step, setStep] = useState('tier');
  
  // Book configuration
  const [selectedTier, setSelectedTier] = useState('standard');
  const [colorScheme, setColorScheme] = useState('classic');
  const [titlePosition, setTitlePosition] = useState('bottom');
  const [dedication, setDedication] = useState('');
  const [coverPhotoUrl, setCoverPhotoUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Narrative generation
  const [narrativeStatus, setNarrativeStatus] = useState('idle'); // 'idle' | 'generating' | 'ready' | 'error'
  const [narrative, setNarrative] = useState(null);
  
  // Preview/checkout
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState(null);

  // Load story data
  useEffect(() => {
    const loadStory = async () => {
      try {
        const storyDoc = await getDoc(doc(db, 'lifeStories', storyId));
        if (storyDoc.exists()) {
          const storyData = { id: storyDoc.id, ...storyDoc.data() };
          setStory(storyData);
          
          // Load saved book settings if they exist
          if (storyData.bookSettings) {
            if (storyData.bookSettings.colorScheme) setColorScheme(storyData.bookSettings.colorScheme);
            if (storyData.bookSettings.titlePosition) setTitlePosition(storyData.bookSettings.titlePosition);
            if (storyData.bookSettings.dedication) setDedication(storyData.bookSettings.dedication);
            if (storyData.bookSettings.coverPhotoUrl) setCoverPhotoUrl(storyData.bookSettings.coverPhotoUrl);
          } else if (storyData.profilePhotoUrl) {
            setCoverPhotoUrl(storyData.profilePhotoUrl);
          }
          
          // Check if narrative already exists
          if (storyData.narrativeBook) {
            setNarrative(storyData.narrativeBook);
            setNarrativeStatus('ready');
          }
        }
        setLoading(false);
      } catch (err) {
        console.error('Error loading story:', err);
        setError('Failed to load story');
        setLoading(false);
      }
    };
    
    loadStory();
    
    // Load contributions for photo count
    const contributionsQuery = query(
      collection(db, 'lifeStories', storyId, 'contributions'),
      orderBy('createdAt', 'desc')
    );
    
    const unsubscribe = onSnapshot(contributionsQuery, (snapshot) => {
      const contribs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setContributions(contribs);
    });
    
    return () => unsubscribe();
  }, [storyId]);

  // Save book settings when they change
  useEffect(() => {
    if (!story || loading) return;
    
    const saveSettings = async () => {
      try {
        await updateDoc(doc(db, 'lifeStories', storyId), {
          bookSettings: {
            colorScheme,
            titlePosition,
            dedication,
            coverPhotoUrl,
            updatedAt: new Date()
          }
        });
      } catch (err) {
        console.error('Error saving book settings:', err);
      }
    };
    
    const timeoutId = setTimeout(saveSettings, 1000);
    return () => clearTimeout(timeoutId);
  }, [colorScheme, titlePosition, dedication, coverPhotoUrl, story, loading, storyId]);

  const getDisplayName = () => {
    if (!story) return '';
    
    // Handle couples - show both names
    if (story.storyType === 'couple' && story.secondPerson?.name) {
      const name1 = story.preferredName || story.residentName || story.subjectName || 'Person 1';
      const name2 = story.secondPerson?.preferredName || story.secondPerson?.name || 'Person 2';
      return `${name1} & ${name2}`;
    }
    
    return story.preferredName || story.residentName || story.subjectName || '';
  };

  const getPhotoCount = () => {
    return contributions.filter(c => c.status === 'approved' && c.type === 'photo').length;
  };

  const handleCoverPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingPhoto(true);
    try {
      const fileName = `cover_${Date.now()}.${file.name.split('.').pop()}`;
      const storageRef = ref(storage, `life-stories/${storyId}/covers/${fileName}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setCoverPhotoUrl(url);
    } catch (err) {
      console.error('Error uploading cover photo:', err);
      setError('Failed to upload photo');
    }
    setUploadingPhoto(false);
  };

  const generateNarrative = async () => {
    setNarrativeStatus('generating');
    setError(null);
    
    try {
      const generateNarrativeBook = httpsCallable(functions, 'generateNarrativeBook');
      const result = await generateNarrativeBook({
        storyId,
        tier: selectedTier
      });
      
      if (result.data.success) {
        setNarrative(result.data.narrative);
        setNarrativeStatus('ready');
      } else {
        throw new Error('Failed to generate narrative');
      }
    } catch (err) {
      console.error('Error generating narrative:', err);
      setError(err.message || 'Failed to generate story narrative');
      setNarrativeStatus('error');
    }
  };

  const generatePreview = async () => {
    setGeneratingPreview(true);
    setError(null);
    
    try {
      const generateBookPreview = httpsCallable(functions, 'generateBookPreview');
      const result = await generateBookPreview({
        storyId,
        tier: selectedTier,
        colorScheme,
        titlePosition,
        dedication,
        coverPhotoUrl
      });
      
      if (result.data.previewUrl) {
        setPreviewUrl(result.data.previewUrl);
        window.open(result.data.previewUrl, '_blank');
      }
    } catch (err) {
      console.error('Error generating preview:', err);
      setError('Failed to generate preview');
    }
    setGeneratingPreview(false);
  };

  const handleCheckout = async () => {
    setCheckingOut(true);
    setError(null);
    
    try {
      const createCheckoutSession = httpsCallable(functions, 'createBookCheckoutSession');
      const result = await createCheckoutSession({
        storyId,
        tier: selectedTier,
        colorScheme,
        titlePosition,
        dedication,
        coverPhotoUrl,
        successUrl: `${window.location.origin}/story/${storyId}/print?success=true`,
        cancelUrl: `${window.location.origin}/story/${storyId}/print?canceled=true`
      });
      
      if (result.data.url) {
        window.location.href = result.data.url;
      }
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start checkout');
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-quiltt-purple animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading print shop...</p>
        </div>
      </div>
    );
  }

  const selectedTierData = BOOK_TIERS.find(t => t.id === selectedTier);
  const selectedColorData = COLOR_SCHEMES.find(c => c.id === colorScheme);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="font-bold text-gray-900">Create {getDisplayName()}'s Book</h1>
              <p className="text-sm text-gray-500">
                {step === 'tier' && 'Choose your book style'}
                {step === 'narrative' && 'Generate your story'}
                {step === 'customize' && 'Personalize your book'}
                {step === 'preview' && 'Review and order'}
              </p>
            </div>
          </div>
          
          {/* Progress steps */}
          <div className="flex items-center gap-2 mt-4">
            {['tier', 'narrative', 'customize', 'preview'].map((s, i) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s 
                    ? 'bg-quiltt-purple text-white' 
                    : ['tier', 'narrative', 'customize', 'preview'].indexOf(step) > i
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {['tier', 'narrative', 'customize', 'preview'].indexOf(step) > i ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    i + 1
                  )}
                </div>
                {i < 3 && (
                  <div className={`w-8 h-1 mx-1 rounded ${
                    ['tier', 'narrative', 'customize', 'preview'].indexOf(step) > i
                      ? 'bg-green-500'
                      : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Step 1: Tier Selection */}
        {step === 'tier' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Book</h2>
              <p className="text-gray-600">Select the perfect format for {getDisplayName()}'s life story</p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              {BOOK_TIERS.map((tier) => {
                const TierIcon = tier.icon;
                const isSelected = selectedTier === tier.id;
                
                return (
                  <button
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id)}
                    className={`relative p-6 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? 'border-quiltt-purple bg-purple-50 shadow-lg'
                        : 'border-gray-200 bg-white hover:border-quiltt-purple/50'
                    }`}
                  >
                    {tier.recommended && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-quiltt-purple text-white text-xs font-medium rounded-full">
                        Most Popular
                      </span>
                    )}
                    
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                      isSelected ? 'bg-quiltt-purple text-white' : 'bg-gray-100 text-gray-600'
                    }`}>
                      <TierIcon className="w-6 h-6" />
                    </div>
                    
                    <h3 className="font-bold text-lg text-gray-900">{tier.name}</h3>
                    <p className="text-sm text-gray-500 mb-3">{tier.description}</p>
                    
                    <div className="text-2xl font-bold text-quiltt-purple mb-4">
                      ${tier.price}
                    </div>
                    
                    <ul className="space-y-2">
                      {tier.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <Check className={`w-4 h-4 ${isSelected ? 'text-quiltt-purple' : 'text-green-500'}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    
                    {isSelected && (
                      <div className="absolute top-4 right-4">
                        <div className="w-6 h-6 bg-quiltt-purple rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setStep('narrative')}
                className="flex items-center gap-2 px-6 py-3 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-quiltt-700 transition-colors"
              >
                Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Narrative Generation */}
        {step === 'narrative' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Generate Your Story</h2>
              <p className="text-gray-600">
                Our AI will transform {getDisplayName()}'s interview responses into a beautiful narrative
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-8 text-center">
              {narrativeStatus === 'idle' && (
                <>
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Sparkles className="w-10 h-10 text-quiltt-purple" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">AI-Powered Storytelling</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Instead of showing raw interview transcripts, we'll craft a cohesive narrative 
                    biography with chapters, pull quotes, and seamlessly integrated photos.
                  </p>
                  <div className="bg-purple-50 rounded-lg p-4 mb-6 max-w-md mx-auto">
                    <h4 className="font-medium text-quiltt-purple mb-2">What you'll get:</h4>
                    <ul className="text-sm text-gray-600 space-y-1 text-left">
                      <li>✨ {selectedTierData?.features[1] || '5 narrative chapters'}</li>
                      <li>💬 Memorable quotes highlighted throughout</li>
                      <li>📸 Photos placed in context with the story</li>
                      <li>❤️ Family memories woven in naturally</li>
                    </ul>
                  </div>
                  <button
                    onClick={generateNarrative}
                    className="inline-flex items-center gap-2 px-8 py-4 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-quiltt-700 transition-colors"
                  >
                    <Sparkles className="w-5 h-5" />
                    Generate {getDisplayName()}'s Story
                  </button>
                </>
              )}

              {narrativeStatus === 'generating' && (
                <>
                  <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Loader2 className="w-10 h-10 text-quiltt-purple animate-spin" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Crafting Your Story...</h3>
                  <p className="text-gray-600 mb-4">
                    Our AI is weaving {getDisplayName()}'s memories into a beautiful narrative.
                  </p>
                  <p className="text-sm text-gray-500">This may take 1-2 minutes</p>
                </>
              )}

              {narrativeStatus === 'ready' && narrative && (
                <>
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Story Generated!</h3>
                  <p className="text-gray-600 mb-6">
                    "{narrative.title}" is ready with {narrative.chapters?.length || 0} chapters
                  </p>
                  
                  {/* Preview of chapters */}
                  <div className="text-left bg-gray-50 rounded-lg p-4 mb-6 max-w-lg mx-auto">
                    <h4 className="font-medium text-gray-900 mb-3">Chapters:</h4>
                    <ol className="space-y-2">
                      {narrative.chapters?.slice(0, 5).map((chapter, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <span className="w-6 h-6 bg-quiltt-purple text-white rounded-full flex items-center justify-center text-xs">
                            {i + 1}
                          </span>
                          <span className="text-gray-700">{chapter.title}</span>
                        </li>
                      ))}
                      {narrative.chapters?.length > 5 && (
                        <li className="text-sm text-gray-500 pl-8">
                          +{narrative.chapters.length - 5} more chapters
                        </li>
                      )}
                    </ol>
                  </div>
                  
                  <button
                    onClick={() => {
                      setNarrativeStatus('idle');
                      setNarrative(null);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-quiltt-purple transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate
                  </button>
                </>
              )}

              {narrativeStatus === 'error' && (
                <>
                  <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-10 h-10 text-red-500" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Generation Failed</h3>
                  <p className="text-gray-600 mb-6">{error || 'Something went wrong. Please try again.'}</p>
                  <button
                    onClick={generateNarrative}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-quiltt-700 transition-colors"
                  >
                    <RefreshCw className="w-5 h-5" />
                    Try Again
                  </button>
                </>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep('tier')}
                className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={() => setStep('customize')}
                disabled={narrativeStatus !== 'ready'}
                className="flex items-center gap-2 px-6 py-3 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-quiltt-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Customize */}
        {step === 'customize' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Personalize Your Book</h2>
              <p className="text-gray-600">Add the finishing touches to {getDisplayName()}'s book</p>
            </div>

            {/* Cover Photo */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Camera className="w-5 h-5 text-quiltt-purple" />
                Cover Photo
              </h3>
              
              <div className="flex items-center gap-6">
                <div 
                  className="w-32 h-40 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                  style={{ backgroundColor: selectedColorData?.bg }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {coverPhotoUrl ? (
                    <img src={coverPhotoUrl} alt="Cover" className="w-full h-full object-cover" />
                  ) : uploadingPhoto ? (
                    <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                  ) : (
                    <div className="text-center p-4">
                      <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <span className="text-xs text-gray-500">Click to upload</span>
                    </div>
                  )}
                </div>
                
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-2">
                    Choose a photo for the cover of {getDisplayName()}'s book
                  </p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="text-quiltt-purple hover:underline text-sm"
                  >
                    {coverPhotoUrl ? 'Change photo' : 'Upload photo'}
                  </button>
                  {story?.profilePhotoUrl && !coverPhotoUrl && (
                    <button
                      onClick={() => setCoverPhotoUrl(story.profilePhotoUrl)}
                      className="ml-4 text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Use profile photo
                    </button>
                  )}
                </div>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleCoverPhotoUpload}
                className="hidden"
              />
            </div>

            {/* Color Scheme */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Palette className="w-5 h-5 text-quiltt-purple" />
                Color Scheme
              </h3>
              
              <div className="grid grid-cols-5 gap-3">
                {COLOR_SCHEMES.map((scheme) => (
                  <button
                    key={scheme.id}
                    onClick={() => setColorScheme(scheme.id)}
                    className={`relative p-3 rounded-lg border-2 transition-all ${
                      colorScheme === scheme.id
                        ? 'border-quiltt-purple'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex gap-1 mb-2">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: scheme.primary }}
                      />
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: scheme.accent }}
                      />
                    </div>
                    <span className="text-xs text-gray-600">{scheme.name}</span>
                    {colorScheme === scheme.id && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-quiltt-purple rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Dedication */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Quote className="w-5 h-5 text-quiltt-purple" />
                Dedication (Optional)
              </h3>
              
              <textarea
                value={dedication}
                onChange={(e) => setDedication(e.target.value)}
                placeholder={`For our beloved ${getDisplayName()}, whose stories fill our hearts...`}
                className="w-full p-4 border border-gray-200 rounded-lg focus:ring-2 focus:ring-quiltt-purple focus:border-transparent resize-none"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-2">
                This will appear on the dedication page of the book
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep('narrative')}
                className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={() => setStep('preview')}
                className="flex items-center gap-2 px-6 py-3 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-quiltt-700 transition-colors"
              >
                Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Preview & Order */}
        {step === 'preview' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Book</h2>
              <p className="text-gray-600">Preview and order {getDisplayName()}'s life story book</p>
            </div>

            {/* Order Summary */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Order Summary</h3>
              
              <div className="flex gap-6">
                {/* Book Preview */}
                <div 
                  className="w-40 h-52 rounded-lg shadow-lg overflow-hidden flex-shrink-0"
                  style={{ backgroundColor: selectedColorData?.bg }}
                >
                  {coverPhotoUrl ? (
                    <div className="relative h-full">
                      <img src={coverPhotoUrl} alt="Cover" className="w-full h-full object-cover" />
                      <div 
                        className="absolute bottom-0 left-0 right-0 p-3"
                        style={{ backgroundColor: `${selectedColorData?.primary}ee` }}
                      >
                        <p className="text-white text-sm font-medium truncate">
                          {narrative?.title || `${getDisplayName()}'s Life Story`}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-full flex items-center justify-center">
                      <Book className="w-16 h-16 text-gray-300" />
                    </div>
                  )}
                </div>
                
                {/* Details */}
                <div className="flex-1">
                  <h4 className="font-bold text-lg text-gray-900">{selectedTierData?.name}</h4>
                  <p className="text-gray-500 text-sm mb-4">{selectedTierData?.pages} pages</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Color scheme:</span>
                      <span className="text-gray-900">{selectedColorData?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Chapters:</span>
                      <span className="text-gray-900">{narrative?.chapters?.length || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Photos included:</span>
                      <span className="text-gray-900">{getPhotoCount()}</span>
                    </div>
                    {dedication && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Dedication:</span>
                        <span className="text-gray-900">Included</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t mt-4 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total:</span>
                      <span className="text-2xl font-bold text-quiltt-purple">
                        ${selectedTierData?.price}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Free shipping included</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={generatePreview}
                disabled={generatingPreview}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 border-2 border-quiltt-purple text-quiltt-purple font-medium rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50"
              >
                {generatingPreview ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating Preview...
                  </>
                ) : (
                  <>
                    <Eye className="w-5 h-5" />
                    Preview Book
                  </>
                )}
              </button>
              
              <button
                onClick={handleCheckout}
                disabled={checkingOut}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-quiltt-purple text-white font-medium rounded-lg hover:bg-quiltt-700 transition-colors disabled:opacity-50"
              >
                {checkingOut ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" />
                    Order Now - ${selectedTierData?.price}
                  </>
                )}
              </button>
            </div>

            <div className="flex justify-start pt-4">
              <button
                onClick={() => setStep('customize')}
                className="flex items-center gap-2 px-6 py-3 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Customize
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
